import { useState } from 'react'
import type { JSX } from 'react'
import { useNavigate } from 'react-router-dom'

import { Dialog, EmptyState, LockIcon, WarningIcon } from '@buffer-mono/popcorn'

import { classifyPostFetchError } from '~publish/helpers/posts/classifyPostFetchError'

/**
 * The "post could not be loaded" presentation, shared by the route-mounted
 * PostErrorDialog below and the in-place PostGroupDialog (which renders it
 * inside its own dialog frame instead of swapping to a second dialog root).
 *
 * Pass nothing when the query resolved without a post; `post` is non-null, so
 * that also means it's gone.
 */
export function PostErrorState({ error }: { error?: unknown }): JSX.Element {
  const failure = classifyPostFetchError(error)

  if (failure === 'accessDenied') {
    return (
      <EmptyState variant="warning">
        <EmptyState.Icon>
          <LockIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Access restricted</EmptyState.Heading>
        <EmptyState.Description>
          You don&apos;t have access to this post. Reach out to your
          organization owner to request access.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  if (failure === 'unavailable') {
    return (
      <EmptyState variant="warning">
        <EmptyState.Icon>
          <WarningIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Post not found</EmptyState.Heading>
        <EmptyState.Description>
          The post you are trying to access does not exist.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  return (
    <EmptyState variant="critical">
      <EmptyState.Icon>
        <WarningIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>Error happened</EmptyState.Heading>
      <EmptyState.Description>
        Error happened fetching the post, please let our team know about it.
      </EmptyState.Description>
    </EmptyState>
  )
}

export function PostErrorDialog({ error }: { error?: unknown }): JSX.Element {
  const [open, setOpen] = useState(true)
  const navigate = useNavigate()

  const onOpenChange = (nextOpen: boolean): void => {
    setOpen(nextOpen)
    if (!nextOpen) {
      navigate('..')
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="medium" style={{ overflow: 'visible', padding: 0 }}>
        <PostErrorState error={error} />
      </Dialog.Content>
    </Dialog>
  )
}
